import ajax from "plugins/ajax";
import i18n from "plugins/i18n";

const store = {
    namespaced: true,
    state: {
        authentication: null,
        pending: {
            login: false,
            logout: false,
            password_lost: false,
            password_recover: false,
            refresh_user_account: false,
            update_user_account: false,
            login_availability: false,
            email_availability: false,
            register: false,
            validate: false,
            add_friends: false,
            remove_friends: false
        }
    },
    actions: {
        /**
         * Login the user.
         *
         * @param {Store} store The Vuex store.
         * @param {Object} credentials The credentials.
         * @return {Object} Ajax response object.
         */
        login: function(store, credentials) {
            return ajax(store, "post", "/users/login", credentials, "LOGIN", false, null, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            }).then(function(response) {
                // We ask to update language with what's coming
                // from server.
                if (response && response.data && response.data.language) {
                    store.commit("language/setLanguage", response.data.language, {
                        root: true
                    });
                }
            });
        },

        /**
         * Logout the user.
         *
         * @param {Store} store The Vuex store.
         * @return {Object} Ajax response object.
         */
        logout: function(store) {
            return ajax(store, "post", "/users/logout", null, "LOGOUT", false, {
                type: "success",
                message: i18n.t("store.message.logout-success"),
                onlyNextRoute: true
            }, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            }).then(function() {
                store.commit("setAuthentication", null);
            });
        },

        /**
         * Ask to recover a password.
         *
         * @param {Store} store The Vuex store.
         * @param {Object} data The object containing email to recover.
         * @return {Object} Ajax response object.
         */
        passwordLost: function(store, data) {
            return ajax(store, "post", "/users/password-lost", data, "PASSWORD_LOST", false, {
                type: "success",
                message: i18n.t("store.message.password-lost-success"),
                onlyNextRoute: true
            }, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Recovering a password lost.
         *
         * @param {Store} store The Vuex store.
         * @param {Object} data The object containing the validation link to recover.
         * @return {Object} Ajax response object.
         */
        passwordRecover: function(store, data) {
            const passwordLostLink = data.passwordLostLink;
            const toSend = {
                password: data.password,
                confirm: data.confirm
            };
            return ajax(store, "post", "/users/password-recover/" + passwordLostLink, toSend, "PASSWORD_RECOVER", false, {
                type: "success",
                message: i18n.t("store.message.password-recover-success"),
                onlyNextRoute: true
            }, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Register a new user.
         *
         * @param {Store} store The Vuex store.
         * @param {Object} user The user data.
         * @return {Object} Ajax response object.
         */
        register: function(store, user) {
            return ajax(store, "post", "/users/register", user, "REGISTER", false, {
                type: "success",
                message: i18n.t("store.message.register-success"),
                onlyNextRoute: true
            }, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Validate a user email.
         *
         * @param {Store} store The Vuex store.
         * @param {String} link The validation link.
         * @return {Object} Ajax response object.
         */
        validate: function(store, link) {
            return ajax(store, "get", "/users/validate/" + link, null, "VALIDATE", false, {
                type: "success",
                message: i18n.t("store.message.validate-success"),
                onlyNextRoute: true
            }, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Update the user account.
         *
         * @param {Store} store The Vuex store.
         * @param {Object} account The data to use to update account.
         * @return {Object} Ajax response object.
         */
        updateUserAccount: function(store, account) {
            return ajax(store, "PUT", "/users", account, "UPDATE_USER_ACCOUNT", false, {
                type: "success",
                message: i18n.t("store.message.update-user-account-success"),
                onlyCurrentRoute: true
            }, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Get the user content.
         *
         * @param {Store} store The Vuex store.
         * @return {Object} Ajax response object.
         */
        refreshUserAccount: function(store) {
            return ajax(store, "GET", "/users", null, "REFRESH_USER_ACCOUNT", false, null, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Check the user's desired login is available or not.
         *
         * @param {Store} store The Vuex store.
         * @param {Object} credentials The credentials.
         * @return {Object} Ajax response object.
         */
        loginAvailability: function(store, credentials) {
            return ajax(store, "POST", "/users/login_availability", credentials, "LOGIN_AVAILABILITY", true, null, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Check the user's desired email is available or not.
         *
         * @param {Store} store The Vuex store.
         * @param {Object} credentials The credentials.
         * @return {Object} Ajax response object.
         */
        emailAvailability: function(store, credentials) {
            return ajax(store, "POST", "/users/email_availability", credentials, "EMAIL_AVAILABILITY", true, null, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Add friends to the user.
         *
         * @param {Store} store The Vuex store.
         * @param {Array} emails The emails of friends to add.
         * @return {Object} Ajax response object.
         */
        addFriends: function(store, emails) {
            return ajax(store, "post", "/users/friends", emails, "ADD_FRIENDS", false, null, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Remove friends to the user.
         *
         * @param {Store} store The Vuex store.
         * @param {Array} emails The emails of friends to remove.
         * @return {Object} Ajax response object.
         */
        removeFriends: function(store, emails) {
            return ajax(store, "delete", "/users/friends", emails, "REMOVE_FRIENDS", false, null, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        }
    },
    mutations: {
        // Login
        API_LOGIN_PENDING: function() {},
        API_LOGIN_FAILURE: function() {},
        API_LOGIN_SUCCESS: function(state, data) {
            state.authentication = data;
        },
        // Logout
        API_LOGOUT_PENDING: function() {},
        API_LOGOUT_FAILURE: function() {},
        API_LOGOUT_SUCCESS: function() {},
        // Password lost
        API_PASSWORD_LOST_PENDING: function() {},
        API_PASSWORD_LOST_FAILURE: function() {},
        API_PASSWORD_LOST_SUCCESS: function() {},
        // Password recover
        API_PASSWORD_RECOVER_PENDING: function() {},
        API_PASSWORD_RECOVER_FAILURE: function() {},
        API_PASSWORD_RECOVER_SUCCESS: function() {},
        // Register
        API_REGISTER_PENDING: function() {},
        API_REGISTER_FAILURE: function() {},
        API_REGISTER_SUCCESS: function() {},
        // Validate
        API_VALIDATE_PENDING: function() {},
        API_VALIDATE_FAILURE: function() {},
        API_VALIDATE_SUCCESS: function() {},
        // Login - availability test
        API_LOGIN_AVAILABILITY_PENDING: function() {},
        API_LOGIN_AVAILABILITY_FAILURE: function() {},
        API_LOGIN_AVAILABILITY_SUCCESS: function() {},
        // Email - availability test
        API_EMAIL_AVAILABILITY_PENDING: function() {},
        API_EMAIL_AVAILABILITY_FAILURE: function() {},
        API_EMAIL_AVAILABILITY_SUCCESS: function() {},
        // Add friends
        API_ADD_FRIENDS_PENDING: function() {},
        API_ADD_FRIENDS_FAILURE: function() {},
        API_ADD_FRIENDS_SUCCESS: function(state, data) {
            state.authentication = data;
        },
        // Remove friends
        API_REMOVE_FRIENDS_PENDING: function() {},
        API_REMOVE_FRIENDS_FAILURE: function() {},
        API_REMOVE_FRIENDS_SUCCESS: function(state, data) {
            state.authentication = data;
        },
        // Reload user
        API_UPDATE_USER_ACCOUNT_PENDING: function() {},
        API_UPDATE_USER_ACCOUNT_FAILURE: function() {},
        API_UPDATE_USER_ACCOUNT_SUCCESS: function(state, data) {
            state.authentication = data;
        },
        // Reload user
        API_REFRESH_USER_ACCOUNT_PENDING: function() {},
        API_REFRESH_USER_ACCOUNT_FAILURE: function() {},
        API_REFRESH_USER_ACCOUNT_SUCCESS: function(state, data) {
            state.authentication = data;
        },

        /**
         * Set authentication elements for the whole application.
         *
         * @param {Object} state The store's state.
         * @param {Object} authentication Any data representing current user.
         */
        setAuthentication: function(state, authentication) {
            state.authentication = authentication;
        },

        /**
         * Set pending state.
         *
         * @param {Object} state The store's state.
         * @param {String} payload The name and value to set.
         */
        setPending: function(state, payload) {
            state.pending[payload.name] = payload.value;
        }
    }
};

export default store;